import React from "react";
import { GridColumn, GridCustomCellProps } from "@progress/kendo-react-grid";
import { useTranslation } from "react-i18next";
import { ImportRawFileListButton, SelectionStatistics, UploadRawFilesButton } from "@presentation/components";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useFileExplorerStore, buildRootBreadcrumbLabel } from "@presentation/stores/FileExplorerStore";
import { useInjection } from "@presentation/components/dependencyInjection";
import { RawFileRepository } from "@infrastructure/repositories";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { useLocation } from "react-router-dom";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { useRawFilesLocationName } from "@presentation/hooks/useStorageLocations";
import { RemoteFolderExplorerGrid, type RemoteFolderExplorerRenderApi } from "@presentation/components/helpers/RemoteFolderExplorerGrid";
import { normalizeFolderKey, normalizePathValue, type ExplorerItem } from "@presentation/components/helpers/useRemoteFolderExplorer";
import { BatchRawFile, PathTypeSchema } from "@domain/schemas/BI";
import { Button } from "@progress/kendo-react-buttons";
import { trashIcon } from "@progress/kendo-svg-icons";
import { useDeleteRawFileDialog, type DeleteTarget } from "@presentation/components/inventory/components/useDeleteRawFileDialog";
import { useProjectPermission } from "@presentation/components/helpers/usePermissionCheck";
import type { UploadDestination } from "@presentation/components/uploadDestination/UploadDestinationPickerDialog";
import { ProjectActions } from "@domain/schemas/workspace";

const DIA_ANALYSIS_ID = "66666666-6666-6666-6666-666666666666";

type SelectedFile = {
  path: string;
  sizeInBytes: number;
  sourcePath?: string | null;
};

/**
`sourcePath` is only present on the actual live-listing payload at runtime - `BatchRawFileSchema` (this
response's declared type) predates `sourcePath` being added, since it's shared with a job's persisted
batch-raw-file records, which never carry one.
*/
const getSourcePath = (rawFile: BatchRawFile) => (rawFile as { sourcePath?: string | null }).sourcePath ?? null;

const buildDeleteTarget = (item: ExplorerItem, projectId: string): DeleteTarget => {
  const segments = item.id.replace(/\/+$/, "").split("/");
  const fileName = segments.pop() ?? item.fileName;
  return { projectId, id: item.id, fileName, folder: segments.join("/"), isFolder: item.isFolder, sourcePath: item.sourcePath };
};

const normalizeScopePrefix = (value?: string | null) => (value ?? "").trim().replaceAll("\\", "/").replace(/^\/+/, "").replace(/\/+$/, "");

/**
Scope membership only - governs what is fetched, stored, and selectable (not the regex view-filter,
which only affects what's currently rendered of what was already fetched). `raw`/`raw_preview`/
`raw_staging` are aliases of the same configured root at the storage layer, so a scope prefix anchored
under one of them must also match items actually returned under either sibling.
*/
const matchesPathScope = (value: string, isFolder: boolean, prefix: string) => {
  if (!prefix) {
    return true;
  }

  const rawRootAliases = ["raw", "raw_preview", "raw_staging"] as const;
  const toScopeAliases = (pathValue: string) => {
    const normalized = normalizePathValue(pathValue);
    if (!normalized) {
      return [""];
    }

    const aliases = new Set<string>([normalized]);
    const segments = normalized.split("/").filter(Boolean);
    const firstSegment = segments[0] ?? "";
    if (rawRootAliases.includes(firstSegment as (typeof rawRootAliases)[number])) {
      const rest = segments.slice(1).join("/");
      for (const candidateRoot of rawRootAliases) {
        aliases.add(rest ? `${candidateRoot}/${rest}` : candidateRoot);
      }
    }

    return [...aliases];
  };

  const normalizedValue = normalizePathValue(value);
  if (!normalizedValue) {
    return false;
  }

  const valueAliases = toScopeAliases(normalizedValue);
  const prefixAliases = toScopeAliases(prefix);

  for (const candidatePrefix of prefixAliases) {
    for (const candidateValue of valueAliases) {
      if (isFolder) {
        if (candidateValue === candidatePrefix || candidateValue.startsWith(`${candidatePrefix}/`) || candidatePrefix.startsWith(`${candidateValue}/`)) {
          return true;
        }
      } else if (candidateValue === candidatePrefix || candidateValue.startsWith(`${candidatePrefix}/`)) {
        return true;
      }
    }
  }

  return false;
};

export const FileExplorerGrid: React.FC<{ select: boolean; allowedExtensions?: string[]; onSelectionChange?: (selectedFiles: SelectedFile[]) => void; pathPrefix?: string; pathRegex?: string }> = ({
  select,
  allowedExtensions,
  onSelectionChange,
  pathPrefix,
  pathRegex,
}) => {
  const rawFileRepository = useInjection(RawFileRepository);
  const activeProjectId = useWorkspaceContextStore((state) => state.activeProjectId);
  const deletePermission = useProjectPermission(activeProjectId, ProjectActions.DeleteJobArtifacts);
  const location = useLocation();
  const { t: loc } = useTranslation();
  const store = useFileExplorerStore();
  const resumeFromStepId = useJobSetupStore((state) => state.CreateJobState.resumeFromStepId);
  const setJobRawFilesInStore = useJobSetupStore((state) => state.setJobRawFiles);
  const isDiaAnalysisRerun = new URLSearchParams(location.search).has("rerunJobId") && resumeFromStepId === DIA_ANALYSIS_ID;
  const normalizedScopePrefix = React.useMemo(() => normalizeScopePrefix(pathPrefix), [pathPrefix]);
  const rawFilesCacheRef = React.useRef<Map<string, Awaited<ReturnType<RawFileRepository["getRawFiles"]>>>>(new Map());
  const rawFilesInFlightRef = React.useRef<Map<string, Promise<Awaited<ReturnType<RawFileRepository["getRawFiles"]>>>>>(new Map());
  const explorerApiRef = React.useRef<RemoteFolderExplorerRenderApi>(null);

  const fileExplorerDebugEnabled = React.useMemo(() => {
    const queryValue = new URLSearchParams(location.search).get("fileExplorerDebug");
    return queryValue === "1" || queryValue === "true" || (typeof window !== "undefined" && localStorage.getItem("of:fileExplorerDebug") === "1");
  }, [location.search]);

  const logFileExplorerDebug = React.useCallback(
    (event: string, details: unknown = {}) => {
      if (!fileExplorerDebugEnabled) {
        return;
      }

      console.info("[FileExplorerDebug]", event, details);
    },
    [fileExplorerDebugEnabled],
  );

  /**
   * Scope membership only (see `matchesPathScope`) - the regex view-filter is handled generically by
   * `RemoteFolderExplorerGrid` itself (project default seeds it, an explicit `pathRegex` prop below
   * overrides that default for this embedding).
   */
  const isItemVisibleInScope = React.useCallback((itemPath: string, isFolder: boolean) => matchesPathScope(itemPath, isFolder, normalizedScopePrefix), [normalizedScopePrefix]);

  // The store seeds the root crumb from /config.json so something is shown immediately; the backend is the
  // source of truth (Azure container / AWS folder), so the label is corrected as soon as it answers.
  const rawFilesLocationName = useRawFilesLocationName();
  const breadcrumbItems = store.breadcrumbItems;
  const setBreadcrumbItems = store.setBreadcrumbItems;
  const initialUploadDestination = React.useMemo<Partial<UploadDestination>>(() => {
    const currentBreadcrumb = breadcrumbItems.at(-1);
    const currentPath = normalizeFolderKey(currentBreadcrumb?.id ?? "").replace(/\/+$/, "");
    const rootPath = normalizePathValue(currentBreadcrumb?.sourcePath ?? "");
    const rootPrefix = rootPath ? `${rootPath}/` : "";
    let folder = currentPath;
    if (currentPath === rootPath) {
      folder = "";
    } else if (rootPrefix && currentPath.startsWith(rootPrefix)) {
      folder = currentPath.slice(rootPrefix.length);
    }

    return { rootPath: rootPath || undefined, folder };
  }, [breadcrumbItems]);
  React.useEffect(() => {
    const rootLabel = buildRootBreadcrumbLabel(rawFilesLocationName);
    const rootItem = breadcrumbItems[0];
    if (!rootItem || rootItem.id !== "home" || rootItem.text === rootLabel) {
      return;
    }

    setBreadcrumbItems(breadcrumbItems.map((item, index) => (index === 0 ? { ...item, text: rootLabel } : item)));
  }, [rawFilesLocationName, breadcrumbItems, setBreadcrumbItems]);

  /**
  Raw's storage container has historically been addressed under 3 sibling root aliases
  (`raw`/`raw_preview`/`raw_staging`) depending on environment/era - this tries every plausible alias
  for the requested folder and returns the first one that actually has content, so browsing never
  silently shows "empty folder" just because the wrong alias was guessed.
  */
  const getRawFileFolderCandidates = React.useCallback((folder: string) => {
    const normalizedFolder = normalizeFolderKey(folder);
    const normalizedFolderNoSlash = normalizedFolder.replace(/\/+$/, "");
    const containerRootFolder = normalizePathValue(runtimeConfig.RAWFILE_CONTAINER_NAME ?? "");
    const candidates = new Set<string>([normalizedFolder]);

    if (!normalizedFolderNoSlash && containerRootFolder) {
      candidates.add(containerRootFolder);
      candidates.add(`${containerRootFolder}/`);
    }

    switch (normalizedFolderNoSlash) {
      case "raw": {
        candidates.add("raw_preview");
        candidates.add("raw_staging");
        candidates.add("raw_preview/");
        candidates.add("raw_staging/");
        break;
      }
      case "raw_preview": {
        candidates.add("raw");
        candidates.add("raw_staging");
        candidates.add("raw/");
        candidates.add("raw_staging/");
        break;
      }
      case "raw_staging": {
        candidates.add("raw");
        candidates.add("raw_preview");
        candidates.add("raw/");
        candidates.add("raw_preview/");
        break;
      }
      default: {
        break;
      }
    }

    if (normalizedFolderNoSlash.startsWith("raw/")) {
      candidates.add(normalizedFolder.replace(/^raw\//, "raw_preview/"));
      candidates.add(normalizedFolder.replace(/^raw\//, "raw_staging/"));
    }

    if (normalizedFolderNoSlash.startsWith("raw_preview/")) {
      candidates.add(normalizedFolder.replace(/^raw_preview\//, "raw/"));
      candidates.add(normalizedFolder.replace(/^raw_preview\//, "raw_staging/"));
    }

    if (normalizedFolderNoSlash.startsWith("raw_staging/")) {
      candidates.add(normalizedFolder.replace(/^raw_staging\//, "raw/"));
      candidates.add(normalizedFolder.replace(/^raw_staging\//, "raw_preview/"));
    }

    const folderWithoutRawRoot = normalizedFolderNoSlash.replace(/^(raw|raw_preview|raw_staging)\//, "");
    if (folderWithoutRawRoot && folderWithoutRawRoot !== normalizedFolderNoSlash) {
      candidates.add(folderWithoutRawRoot);
      candidates.add(`${folderWithoutRawRoot}/`);
    }

    if (folderWithoutRawRoot && !normalizedFolderNoSlash.startsWith("raw") && !normalizedFolderNoSlash.startsWith("raw_preview") && !normalizedFolderNoSlash.startsWith("raw_staging")) {
      candidates.add(`raw/${folderWithoutRawRoot}`);
      candidates.add(`raw_preview/${folderWithoutRawRoot}`);
      candidates.add(`raw_staging/${folderWithoutRawRoot}`);
      candidates.add(`raw/${folderWithoutRawRoot}/`);
      candidates.add(`raw_preview/${folderWithoutRawRoot}/`);
      candidates.add(`raw_staging/${folderWithoutRawRoot}/`);
    }

    return [...candidates].filter((candidate) => candidate !== "home");
  }, []);

  const getRawFilesWithCache = React.useCallback(
    async (folder: string, sourcePath: string | null, shouldForceRefresh: boolean) => {
      const normalizedFolder = normalizeFolderKey(folder);
      const cacheKey = `${activeProjectId}::${normalizedFolder}::${sourcePath ?? ""}`;

      if (shouldForceRefresh) {
        rawFilesCacheRef.current.delete(cacheKey);
        rawFilesInFlightRef.current.delete(cacheKey);
      }

      const cachedResponse = rawFilesCacheRef.current.get(cacheKey);
      if (cachedResponse) {
        return cachedResponse;
      }

      const inFlightRequest = rawFilesInFlightRef.current.get(cacheKey);
      if (inFlightRequest) {
        return inFlightRequest;
      }

      if (!activeProjectId) {
        return { list: [] };
      }

      const request = (async () => {
        try {
          const response = await rawFileRepository.getRawFiles(activeProjectId, normalizedFolder, sourcePath);
          rawFilesCacheRef.current.set(cacheKey, response);
          return response;
        } finally {
          rawFilesInFlightRef.current.delete(cacheKey);
        }
      })();

      rawFilesInFlightRef.current.set(cacheKey, request);
      return request;
    },
    [activeProjectId, rawFileRepository],
  );

  const clearRawFilesCache = React.useCallback(() => {
    rawFilesCacheRef.current.clear();
    rawFilesInFlightRef.current.clear();
  }, []);

  const fetchList = React.useCallback(
    async (folder: string, sourcePath: string | null, shouldForceRefresh: boolean) => {
      const candidates = getRawFileFolderCandidates(folder);
      let firstResponse: Awaited<ReturnType<RawFileRepository["getRawFiles"]>> | null = null;
      let firstError: unknown = null;

      logFileExplorerDebug("fetch-start", {
        requestedFolder: folder,
        normalizedFolder: normalizeFolderKey(folder),
        candidates,
        sourcePath,
        forceRefresh: shouldForceRefresh,
        scopePrefix: normalizedScopePrefix,
      });

      for (const [index, candidate] of candidates.entries()) {
        try {
          const response = await getRawFilesWithCache(candidate, sourcePath, shouldForceRefresh);
          const count = response.list?.length ?? 0;

          logFileExplorerDebug("fetch-candidate", { candidate, count });

          if (index === 0) {
            firstResponse = response;
          }

          if (count > 0) {
            logFileExplorerDebug("fetch-hit", { candidate, count });
            return response;
          }
        } catch (error) {
          if (index === 0) {
            firstError = error;
          }

          logFileExplorerDebug("fetch-candidate-error", { candidate, message: error instanceof Error ? error.message : String(error) });
        }
      }

      logFileExplorerDebug("fetch-empty", { requestedFolder: folder, candidates });

      if (firstResponse) {
        return firstResponse;
      }

      if (firstError) {
        throw firstError;
      }

      return getRawFilesWithCache(folder, sourcePath, shouldForceRefresh);
    },
    [getRawFileFolderCandidates, getRawFilesWithCache, logFileExplorerDebug, normalizedScopePrefix],
  );

  const handleRawFileDeleted = React.useCallback(
    async (deleted: DeleteTarget) => {
      const { selectedState, setSelectedState } = useFileExplorerStore.getState();
      const descendantPrefix = `${deleted.id.replace(/\/+$/, "")}/`;
      const remaining = Object.fromEntries(
        Object.entries(selectedState).filter(([, metadata]) => {
          const isSameRoot = (metadata.sourcePath ?? null) === (deleted.sourcePath ?? null);
          return !isSameRoot || (metadata.path !== deleted.id && !(deleted.isFolder && metadata.path.startsWith(descendantPrefix)));
        }),
      );
      setSelectedState(remaining);
      const selectedFiles = Object.values(remaining).filter((file) => !file.isFolder);
      setJobRawFilesInStore(selectedFiles);
      onSelectionChange?.(selectedFiles);
      clearRawFilesCache();
      if (!explorerApiRef.current) {
        throw new Error("The raw file explorer is no longer available to refresh.");
      }
      await explorerApiRef.current.refresh();
    },
    [clearRawFilesCache, onSelectionChange, setJobRawFilesInStore],
  );
  const { deleteRawFileDialog, openDeleteDialog } = useDeleteRawFileDialog({ projectId: activeProjectId, onDeleted: handleRawFileDeleted });

  return (
    <RemoteFolderExplorerGrid<BatchRawFile>
      apiRef={explorerApiRef}
      activeProjectId={activeProjectId}
      pathType={PathTypeSchema.Raw}
      selectionMode="multi"
      regexFilterStorageKey="rawFilesRegex"
      regexDefaultOverride={pathRegex}
      fileNameColumnTitle={loc("fileName")}
      showSelectionColumn={select}
      preventDeselection={isDiaAnalysisRerun}
      allowedExtensions={allowedExtensions}
      isItemVisibleInScope={isItemVisibleInScope}
      store={{ ...store, items: store.rawFiles, setItems: store.setRawFiles }}
      fetchList={fetchList}
      toExplorerItem={(rawFile) => {
        const location = String(rawFile.location ?? "").replaceAll("\\", "/");
        const fileName = String(rawFile.fileName ?? "");
        const isStructuralFolder = fileName.trim().length === 0;
        const name = isStructuralFolder ? (location.replace(/\/$/, "").split("/").pop() ?? "") : fileName;
        // `.d` folders are listed as folders by the API but treated as a single opaque file here - not
        // recursed into, not separately browsable.
        const isDotDFolder = isStructuralFolder && name.endsWith(".d");
        return {
          location: rawFile.location,
          fileName: rawFile.fileName,
          sizeInBytes: rawFile.sizeInBytes,
          sourcePath: getSourcePath(rawFile),
          fileCount: rawFile.fileCount,
          isFolderOverride: isStructuralFolder && !isDotDFolder,
        };
      }}
      onSelectionChange={(selected) => {
        setJobRawFilesInStore(selected);
        onSelectionChange?.(selected);
      }}
      toolbarActions={(api) => (
        <>
          <UploadRawFilesButton
            projectId={activeProjectId ?? ""}
            initialDestination={initialUploadDestination}
            onUploadSuccess={() => {
              clearRawFilesCache();
              return api.refresh();
            }}
          />
          <ImportRawFileListButton />
        </>
      )}
      dialog={() => deleteRawFileDialog}
      aboveGrid={
        select && (
          <div style={{ flex: "0 0 auto" }}>
            {isDiaAnalysisRerun && (
              <div style={{ padding: "8px 12px", backgroundColor: "#fff8e1", color: "#8a6d1f", borderRadius: "3px", marginBottom: "8px", fontSize: "13px" }}>
                Additions here are applied only to the DIA Analysis step for this restart. Removing raw files is disabled.
              </div>
            )}
            <SelectionStatistics />
          </div>
        )
      }
      columnsAfterLocation={
        <GridColumn
          field="fileCount"
          title={loc("fileCount")}
          width="150px"
          filterable={false}
          cell={(properties: GridCustomCellProps) => (
            <td>{properties.dataItem.fileName.endsWith(".d") || properties.dataItem.fileCount === null || properties.dataItem.fileCount === undefined ? "-" : properties.dataItem.fileCount}</td>
          )}
        />
      }
    >
      <GridColumn
        title={loc("actions")}
        width="90px"
        filterable={false}
        sortable={false}
        cell={(properties: GridCustomCellProps) => {
          const item = properties.dataItem as ExplorerItem;
          const isSelected = Object.values(store.selectedState).some(
            (selected) => (selected.sourcePath ?? null) === (item.sourcePath ?? null) && (selected.path === item.id || (item.isFolder && selected.path.startsWith(`${item.id.replace(/\/+$/, "")}/`))),
          );
          return (
            <td style={{ textAlign: "center" }}>
              <Button
                fillMode="flat"
                themeColor="error"
                svgIcon={trashIcon}
                disabled={!item.id || !activeProjectId || !deletePermission.allowed || (isDiaAnalysisRerun && isSelected)}
                title={`Delete ${item.fileName}`}
                aria-label={`Delete ${item.isFolder ? "folder" : "file"} ${item.fileName}`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (activeProjectId) {
                    void openDeleteDialog(buildDeleteTarget(item, activeProjectId));
                  }
                }}
              />
            </td>
          );
        }}
      />
    </RemoteFolderExplorerGrid>
  );
};

export default FileExplorerGrid;
